'use client';
import { polymorphicFactory, useUncontrolled, Flex, DsmGridSpacing, DsmColor, Box, Tab } from '@kohler-dsm-library/kohler-core';
import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';
import classes from './TabbedNavigationHorizontal.module.css.mjs';

const TabbedNavigationHorizontal = polymorphicFactory(
  (props, ref) => {
    const { mod, tabs, align, component = "div", value, initialSelectedTab, onTabClick, className, ...rest } = props;
    const firstTabId = tabs[0]?.id ?? tabs[0]?.text;
    const resolvedDefault = initialSelectedTab ?? firstTabId;
    const handleChange = (val) => {
      onTabClick?.(val);
    };
    const [selectedTab, setSelectedTab] = useUncontrolled({
      value,
      defaultValue: resolvedDefault,
      finalValue: firstTabId,
      onChange: handleChange
    });
    const [underlineStyle, setUnderlineStyle] = useState({});
    const tabRefs = useRef([]);
    const [isInitialRender, setIsInitialRender] = useState(true);
    useEffect(() => {
      const selectedTabElement = tabRefs.current.find((tab) => tab.id === selectedTab);
      if (selectedTabElement) {
        setUnderlineStyle({
          width: `${selectedTabElement.offsetWidth}px`,
          transform: `translateX(${selectedTabElement.offsetLeft}px)`
        });
      }
    }, [selectedTab, align, tabs]);
    return /* @__PURE__ */ React.createElement(
      Flex,
      {
        role: "tablist",
        mod,
        component,
        className: clsx("kohler-tabbed-navigation-horizontal-root", className, classes.tabNavigationHorizontal),
        align: "flex-start",
        margin: 0,
        borderBottom: "1px solid var(--border-neutral-medium-contrast)",
        position: "relative",
        backgroundColor: DsmColor.SurfaceTransparent,
        width: "100vw",
        maxWidth: "100%",
        whiteSpace: "nowrap",
        style: { listStyle: "none" },
        paddingTop: 18,
        paddingInline: DsmGridSpacing.DefaultMargin,
        ref,
        ...rest
      },
      /* @__PURE__ */ React.createElement(Flex, { className: align === "center" && classes.tabNavigationHorizontalCenter }, /* @__PURE__ */ React.createElement(
        Box,
        {
          backgroundColor: DsmColor.BorderPrimary,
          height: 4,
          position: "absolute",
          style: underlineStyle,
          className: clsx(classes.underline, isInitialRender && classes.underlineNoTransition)
        }
      ), tabs.map(({ id, text, ...restTabProps }, index) => {
        const tabId = id || text;
        const isSelected = selectedTab === tabId;
        return /* @__PURE__ */ React.createElement(
          Tab,
          {
            role: "tab",
            type: "button",
            id: tabId,
            key: tabId,
            text,
            "aria-selected": isSelected,
            "aria-expanded": isSelected,
            ref: (el) => tabRefs.current[index] = el,
            onClick: () => {
              if (isInitialRender) {
                setIsInitialRender(false);
              }
              setSelectedTab(tabId);
              onTabClick?.(tabId);
            },
            ...restTabProps
          }
        );
      }))
    );
  }
);

export { TabbedNavigationHorizontal };
